import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { EntityUpdate } from '../taskAuthorizations';
import { compose, wfpFormat } from '../../utils/utils';
import { paymentFrequencyMap } from '../../utils/paymentFrequencyMap';
import State from '../../app/store/state';
import { Permission } from '../../app/appConfig';
import { DisplayPublicGpgKey } from '../../managers/helpers/DisplayPublicGpgKey';
import { loadProductImage } from '../../apiClient';

interface Props {
    updates: EntityUpdate[];
    isActivityLogsView?: boolean;
    entityId?: string;
}

interface PermissionsWithSections {
    [key: string]: Permission[];
}

export function DefaultChangesList({ updates, isActivityLogsView, entityId }: Props) {
    const allPermissions = useSelector((state: State) => state.appConfig.permissions);
    const publicCdn = useSelector((state: State) => state.appConfig.awsConfig?.publicAssetsCDN);
    const [oldImageUrl, setOldImageUrl] = useState<string | null>(null);

    useEffect(() => {
        const hasImageChange = updates?.some((u) => u.path === 'imageStageKey');
        if (!hasImageChange || !entityId) {
            setOldImageUrl(null);
            return;
        }
        (async () => {
            try {
                const res = await loadProductImage(entityId);
                const url = (res as { url?: string })?.url;
                setOldImageUrl(url || null);
            } catch {
                setOldImageUrl(null);
            }
        })();
    }, [entityId, updates]);

    return (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
            <table className={`wfp-table--striped ${isActivityLogsView ? '' : 'mt4 table-hover'}`}>
                <thead>
                    <tr>
                        <th>Property</th>
                        <th>Old value</th>
                        <th>New value</th>
                    </tr>
                </thead>
                <tbody>
                    {updates
                        .filter(
                            (update) =>
                                JSON.stringify(update.newValue) !== JSON.stringify(update.oldValue) &&
                                update['path'] !== 'updatedAt' &&
                                update['path'] !== 'authorizedAt'
                        )
                        .map((update) => displayUpdate(update, allPermissions, publicCdn, oldImageUrl))}
                </tbody>
            </table>
        </div>
    );
}

function displayUpdate(
    update: EntityUpdate,
    permissions: PermissionsWithSections,
    publicCdn?: string,
    oldImageUrl?: string | null
) {
    const { oldValue, newValue } = displayedValues(update, permissions, publicCdn, oldImageUrl);
    return (
        <tr key={update.path}>
            <td>{update.path ? wfpFormat(update.path) : '-'}</td>
            <td>{oldValue}</td>
            <td>{newValue}</td>
        </tr>
    );
}

function displayedValues(
    update: EntityUpdate,
    permissions: PermissionsWithSections,
    publicCdn?: string,
    oldImageUrl?: string | null
): { oldValue: React.ReactNode; newValue: React.ReactNode } {
    if (update.path === 'featuresAccess' || update.path === 'permissions') {
        return displayedPermissions(update, permissions);
    } else if (update.path === 'paymentFrequency') {
        return displayedPaymentFrequency(update);
    } else if (update.path === 'expirationDate' || update.path === 'startDate') {
        return displayedDate(update);
    } else if (update.path === 'profile') {
        return displayedProfile(update);
    } else if (update.path === 'publicGpgKey') {
        return displayedGpgPublicKey(update);
    } else if (update.path === 'imageStageKey') {
        const stagedKey = update.newValue as string;
        const cdn = publicCdn || '';
        const newUrl = cdn ? `${cdn}/${stagedKey}` : '';
        return {
            oldValue: oldImageUrl ? (
                <img
                    alt="Current product image"
                    src={oldImageUrl}
                    style={{
                        maxWidth: 240,
                        maxHeight: 240,
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: 4,
                        padding: 4,
                    }}
                />
            ) : (
                <span>-</span>
            ),
            newValue: newUrl ? (
                <img
                    alt="New product image"
                    src={newUrl}
                    style={{
                        maxWidth: 240,
                        maxHeight: 240,
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: 4,
                        padding: 4,
                    }}
                />
            ) : (
                <span>Preview unavailable</span>
            ),
        };
    } else if (update.path === 'status' && update.oldValue === 'blocked' && update.newValue === 'active') {
        return displayedMobileDeviceUnBlock();
    } else {
        return {
            oldValue: compose<unknown, string>(wfpFormat, displayValue)(update.oldValue),
            newValue: compose<unknown, string>(wfpFormat, displayValue)(update.newValue),
        };
    }
}

function displayedGpgPublicKey(update: EntityUpdate) {
    return {
        oldValue: (
            <div style={{ maxWidth: '300px', overflowWrap: 'break-word', wordWrap: 'break-word', hyphens: 'auto' }}>
                <DisplayPublicGpgKey publicGpgKey={update.oldValue} />
            </div>
        ),
        newValue: (
            <div style={{ maxWidth: '300px', overflowWrap: 'break-word', wordWrap: 'break-word', hyphens: 'auto' }}>
                <DisplayPublicGpgKey publicGpgKey={update.newValue} />
            </div>
        ),
    };
}

function displayedPermissions(
    update: EntityUpdate,
    permissions: PermissionsWithSections
): { oldValue: React.ReactNode; newValue: React.ReactNode } {
    const sections = {} as Record<string, { displayText: string }>;

    Object.values(permissions).forEach((section) => {
        section.forEach((permission) => {
            sections[permission.permissionName] = permission.metadata;
        });
    });

    const addedValues =
        update.newValue && update.newValue.length > 0
            ? update.newValue.filter((permission) => !update.oldValue.includes(permission))
            : [];
    const removedValues =
        update.oldValue && update.oldValue.length > 0
            ? update.oldValue.filter((permission) => !update.newValue.includes(permission))
            : [];
    const unchangedValues =
        update.oldValue && update.oldValue.length > 0
            ? update.oldValue.filter(
                  (permission) => !addedValues.includes(permission) && !removedValues.includes(permission)
              )
            : [];

    const oldValues = [] as React.ReactNode[];
    unchangedValues.forEach((permission, index) =>
        sections[permission]
            ? oldValues.push(<p key={`old/unchanged/${index}/` + permission}>{sections[permission].displayText}</p>)
            : null
    );
    removedValues.forEach((permission, index) =>
        sections[permission]
            ? oldValues.push(
                  <p key={`old/removed/${index}/` + permission} style={{ color: 'red' }}>
                      {sections[permission].displayText}
                  </p>
              )
            : null
    );
    addedValues.forEach((permission, index) =>
        oldValues.push(
            <p key={`old/added/${index}/` + permission} style={{ color: 'green' }}>
                -
            </p>
        )
    );

    const newValues = [] as React.ReactNode[];
    unchangedValues.forEach((permission, index) =>
        sections[permission]
            ? newValues.push(<p key={`new/unchanged/${index}/` + permission}>{sections[permission].displayText}</p>)
            : null
    );
    removedValues.forEach((permission, index) =>
        newValues.push(
            <p key={`new/removed/${index}/` + permission} style={{ color: 'red' }}>
                -
            </p>
        )
    );
    addedValues.forEach((permission, index) =>
        sections[permission]
            ? newValues.push(
                  <p key={`new/added/${index}/` + permission} style={{ color: 'green' }}>
                      {sections[permission].displayText}
                  </p>
              )
            : null
    );

    return {
        oldValue: <div>{oldValues}</div>,
        newValue: <div>{newValues}</div>,
    };
}

function displayValue(value) {
    if (value instanceof Array) {
        return (
            <div>
                {value.map((text) => (
                    // eslint-disable-next-line react/jsx-key
                    <div>{text}</div>
                ))}
            </div>
        );
    }
    if (value === true) {
        return 'Yes';
    }
    if (value === false) {
        return 'No';
    }
    return value;
}

function displayedPaymentFrequency(update: EntityUpdate): { oldValue: React.ReactNode; newValue: React.ReactNode } {
    return {
        oldValue: paymentFrequencyMap[update.oldValue],
        newValue: paymentFrequencyMap[update.newValue],
    };
}

function displayedDate(update: EntityUpdate): { oldValue: React.ReactNode; newValue: React.ReactNode } {
    return {
        oldValue: update.oldValue ? update.oldValue.slice(0, 10) : '-',
        newValue: update.newValue ? update.newValue.slice(0, 10) : '-',
    };
}

function displayedProfile(update: EntityUpdate): { oldValue: React.ReactNode; newValue: React.ReactNode } {
    return {
        oldValue: update.oldValue ? `(ID: ${update.oldValue.id}) ${update.oldValue.name}` : '-',
        newValue: update.newValue ? `(ID: ${update.newValue.id}) ${update.newValue.name}` : '-',
    };
}

function displayedMobileDeviceUnBlock(): { oldValue: React.ReactNode; newValue: React.ReactNode } {
    return {
        oldValue: 'Blocked',
        newValue: 'Unblocked',
    };
}
